import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loading } from "@/components/Common";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import TitlePage from "@/pages/TitlePage";
import { Login, Register, Forgot, Reset, AuthCallback } from "@/pages/Auth";
import { Community, PostPage } from "@/pages/Community";
import { Watchlist, CollectionPage, Profile, Settings, Notifications, MyReviews } from "@/pages/Member";
import NotFound, { StaticPage, About, Contact, WhereToWatchPage } from "@/pages/Static";
import Admin from "@/pages/admin/Admin";

function RequireAuth({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <NotFound />;
  return children;
}

function ScrollAndSource() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    const p = new URLSearchParams(search);
    if (p.get("utm_source")) sessionStorage.setItem("utm_source", p.get("utm_source"));
    if (sessionStorage.getItem("first_ref") === null) sessionStorage.setItem("first_ref", document.referrer.includes(window.location.host) ? "" : document.referrer);
  }, [search]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      <ScrollAndSource />
      {!isAdmin && <Header />}
      <main className="min-h-[70vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/movies" element={<Browse kind="movies" />} />
          <Route path="/tv-shows" element={<Browse kind="tv-shows" />} />
          <Route path="/anime" element={<Browse kind="anime" />} />
          <Route path="/browse/:key" element={<Browse />} />
          <Route path="/movie/:slug" element={<TitlePage typePath="movie" />} />
          <Route path="/tv-show/:slug" element={<TitlePage typePath="tv-show" />} />
          <Route path="/anime/:slug" element={<TitlePage typePath="anime" />} />
          <Route path="/where-to-watch" element={<WhereToWatchPage />} />
          <Route path="/community" element={<Community />} />
          <Route path="/community/post/:id" element={<PostPage />} />
          <Route path="/collection/:id" element={<CollectionPage />} />
          <Route path="/u/:username" element={<Profile />} />
          <Route path="/watchlist" element={<RequireAuth><Watchlist /></RequireAuth>} />
          <Route path="/my-reviews" element={<RequireAuth><MyReviews /></RequireAuth>} />
          <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<Forgot />} />
          <Route path="/reset-password" element={<Reset />} />
          <Route path="/auth/callback" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/page/:slug" element={<StaticPage />} />
          <Route path="/admin/*" element={<RequireAuth admin><Admin /></RequireAuth>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
